import type { CustomerDto } from '@tk/shared';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import AsiakasKoodi from './AsiakasKoodi';
import { useKaannos } from '../lib/i18n';
import { useCustomerSearch } from '../lib/queries';
import { useSession } from '../lib/session';

/**
 * Asiakasvalitsin on hakukenttä, ei pudotusvalikko (suunnitelma 10).
 *
 * Satojen asiakkaiden listaa ei ladata selaimeen; kirjoittaminen hakee
 * palvelimelta koodilla ja nimellä. Asiakasvastaavalle näkyvät kaikki
 * asiakkaat — hän kirjaa tunteja muillekin kuin vastuuasiakkailleen —
 * mutta omat vastuuasiakkaat nousevat listan kärkeen (2.4).
 */
export default function AsiakasValitsin({
  valittu,
  onValitse,
  autoFocus = false,
}: {
  valittu: CustomerDto | null;
  onValitse: (customer: CustomerDto | null) => void;
  autoFocus?: boolean;
}) {
  const { user } = useSession();
  const { t, kieli } = useKaannos();
  const [haku, setHaku] = useState('');
  const [auki, setAuki] = useState(false);
  const [viivastetty, setViivastetty] = useState('');

  // Näppäilyn viivästys: hakukutsu jokaisesta merkistä kuormittaisi
  // palvelinta turhaan eikä ehtisi näkyä käyttäjälle.
  useEffect(() => {
    const timer = setTimeout(() => setViivastetty(haku.trim()), 200);
    return () => clearTimeout(timer);
  }, [haku]);

  const { data, isPending } = useCustomerSearch(viivastetty, { enabled: auki });

  const omatIdt = useMemo(
    () => new Set(user.assignedCustomers.map((customer) => customer.id)),
    [user.assignedCustomers],
  );

  const tulokset = useMemo(() => {
    const items = data?.items ?? [];
    return [...items].sort((a, b) => {
      const omaEro = Number(omatIdt.has(b.id)) - Number(omatIdt.has(a.id));
      return omaEro !== 0 ? omaEro : a.name.localeCompare(b.name, kieli);
    });
  }, [data, omatIdt, kieli]);

  if (valittu && !auki) {
    return (
      <div className="border-reuna bg-pinta flex items-center justify-between rounded-md border px-3 py-2">
        <span className="text-teksti flex items-center gap-1.5 text-sm">
          <AsiakasKoodi code={valittu.code} colorKey={valittu.colorKey} />
          {valittu.name}
        </span>
        <button
          type="button"
          onClick={() => {
            setHaku('');
            setAuki(true);
          }}
          className="text-korostus text-xs hover:underline"
        >
          {t('asiakasValitsin.vaihda')}
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search
        className="text-teksti-vaimea pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2"
        aria-hidden="true"
      />
      <input
        type="text"
        value={haku}
        autoFocus={autoFocus}
        placeholder={t('asiakasValitsin.hae')}
        role="combobox"
        aria-expanded={auki}
        aria-controls="asiakaslista"
        onFocus={() => setAuki(true)}
        onChange={(event) => {
          setHaku(event.target.value);
          setAuki(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && valittu) {
            event.stopPropagation();
            setAuki(false);
          }
        }}
        className="border-reuna bg-pinta text-teksti w-full rounded-md border py-2 pl-9 pr-3 text-sm"
      />

      {auki && (
        <ul
          id="asiakaslista"
          role="listbox"
          className="border-reuna bg-pinta-korotettu absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border shadow-lg"
        >
          {isPending && (
            <li className="text-teksti-vaimea px-3 py-2 text-sm">{t('yleinen.haetaan')}</li>
          )}
          {!isPending && tulokset.length === 0 && (
            <li className="text-teksti-vaimea px-3 py-2 text-sm">{t('yleinen.eiOsumia')}</li>
          )}
          {tulokset.map((customer) => (
            <li key={customer.id}>
              <button
                type="button"
                role="option"
                aria-selected={customer.id === valittu?.id}
                onClick={() => {
                  onValitse(customer);
                  setAuki(false);
                  setHaku('');
                }}
                className="hover:bg-korostus-pehmea flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
              >
                {/* Sävy opitaan siellä, missä asiakas valitaan: listasta
                  näkee, minkä värisenä juuri valittu asiakas ilmestyy
                  kalenteriin. Siru on omassa kiinteän levyisessä
                  solussaan, jotta nimet pysyvät allekkain. */}
                <span className="w-20 shrink-0">
                  <AsiakasKoodi code={customer.code} colorKey={customer.colorKey} />
                </span>
                <span className="text-teksti flex-1">{customer.name}</span>
                {omatIdt.has(customer.id) && (
                  <span className="text-korostus text-xs">{t('asiakasValitsin.vastuullasi')}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
